import { useState, useEffect } from 'react';
import {FaTrash, FaCheckCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import {IoIosAddCircleOutline, IoMdCreate} from 'react-icons/io'; 
import AñadirResultados from './AnadirResultados';
import EditarResultados from './EditarResultados';
import styles from './Resultados.module.css'; 
import {useAuth} from '../../../Auth/AuthContext'; 

const Resultados = ({ testId }) => {
    const {getToken} = useAuth(); 
    const token = getToken(); 
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModalAgregar, setShowModalAgregar] = useState(false);
    const [showModalEditar, setShowModalEditar] = useState(false);
    const [areaToEdit, setAreaToEdit] = useState(null);

    useEffect(() => {
        if (testId) {
            fetchAreas();
        }
    }, [testId]);

    const fetchAreas = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/${testId}/areas`, { 
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                let errorMessage = `Error ${response.status}: ${response.statusText}`;

                try {
                    const errorData = await response.json();
                    if (errorData.error && errorData.message) {
                        errorMessage = `${errorData.error}: ${errorData.message}`;
                    } else if (errorData.message) {
                        errorMessage = errorData.message;
                    }
                } catch (parseError) {
                    console.warn('No se pudo parsear el error del backend:', parseError);
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();
            setAreas(data);
        } catch (err) {
            setError(err.message);
            console.error('Error al obtener áreas:', err);

            Swal.fire({
                title: 'Error al cargar áreas',
                text: err.message,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57', 
                customClass: { 
                    popup: 'popupbonito', 
                },
            });
        } finally {
            setLoading(false);
        }
    };

    const getMaxOrden = () => {
        return areas.length > 0 ? Math.max(...areas.map(a => a.ar_orden || 0)) : 0;
    };

    const handleAddArea = () => {
        setShowModalAgregar(true);
    };

    const handleCloseModalAgregar = () => {
        setShowModalAgregar(false);
    };

    const handleSuccessAdd = () => {
        fetchAreas();
    };

    const handleEditArea = (area) => {
        setAreaToEdit(area);
        setShowModalEditar(true);
    };

    const handleCloseModalEditar = () => {
        setShowModalEditar(false);
        setAreaToEdit(null);
    };

    const handleSuccessEdit = () => {
        fetchAreas();
    };

    const handleDeleteArea = async (areaId, areaCodigo, areaNombre) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: `Se eliminará el área "${areaNombre}" (${areaCodigo}). Esta acción no se puede deshacer.`,
            icon: 'warning',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito', 
            },
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/test/areas/eliminar-area/${areaId}`, {
                    method: 'DELETE',
                    headers: { 
                    'Authorization': `Bearer ${token}`, 
                    }, 
                });

                if (response.ok) {
                    fetchAreas();

                    Swal.fire({
                        title: '¡Eliminado!',
                        text: `El área "${areaNombre}" ha sido eliminada correctamente.`,
                        icon: 'success',
                        showConfirmButton: false, 
                        timer: 1000,
                        customClass: { 
                            popup: 'popupbonito', 
                        },
                        });
                } else {
                    let errorTitle = 'Error';
                    let errorMessage = 'No se pudo eliminar el área';

                    try {
                        const errorData = await response.json();

                        if (errorData.error && errorData.message) {
                            errorTitle = errorData.error;
                            errorMessage = errorData.message;
                        } else if (errorData.message) {
                            errorMessage = errorData.message;
                        }

                        switch (response.status) {
                            case 400:
                                if (errorMessage.includes('respuestas asociadas')) {
                                    errorTitle = 'No se puede eliminar';
                                } else if (errorMessage.includes('ID de área inválido')) {
                                    errorTitle = 'ID inválido';
                                }
                                break;
                            case 404:
                                errorTitle = 'Área no encontrada';
                                break;
                            case 500:
                                errorTitle = 'Error del servidor';
                                break;
                        }

                    } catch (parseError) {
                        errorMessage = `Error ${response.status}: ${response.statusText}`;
                    }

                    Swal.fire({
                        title: errorTitle,
                        text: errorMessage,
                        icon: 'error',
                        confirmButtonText: 'Entendido',
                        confirmButtonColor: '#242b57', 
                        customClass: { 
                            popup: 'popupbonito', 
                        },
                    });
                }
            } catch (error) {
                console.error('Error al eliminar área:', error);

                Swal.fire({
                    title: 'Error de conexión',
                    text: 'No se pudo conectar con el servidor. Verifique su conexión a internet.',
                    icon: 'error',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57', 
                    customClass: { 
                        popup: 'popupbonito', 
                    },
                });
            }
        }
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Cargando resultados...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
        <div className={styles.loadingContainer}>
            <div className={styles.loadingContainer}> 
            <p className={styles.loadingText}> Error al cargar datos :( </p>
            </div>
        </div>
        );
    }

    return (
        <div className={styles.resultadosContainer}>

            <AñadirResultados
                testId={testId}
                isOpen={showModalAgregar}
                onClose={handleCloseModalAgregar}
                onSuccess={handleSuccessAdd}
                getMaxOrden={getMaxOrden}
            />

            <EditarResultados
                area={areaToEdit}
                isOpen={showModalEditar}
                onClose={handleCloseModalEditar}
                onSuccess={handleSuccessEdit}
            />

            <div className={styles.resultadosContainer}>
                <div className={styles.resultadosHeader}>
                    <div className={styles.headerInfo}>
                        <div>
                            <h3>Configuración de Resultados</h3>
                            <p>Aquí puedes añadir, editar o eliminar los posibles resultados que los usuarios pueden obtener al finalizar el test.</p>
                            <p>En el campo "Código" puedes ingresar 1 o 2 caracteres para identificar el área o resultado al que pertenecerán ciertas preguntas. Esto facilita la ponderación y permite obtener resultados más precisos.</p>
                            <p>Recuerda asignar un área a las preguntas que se hayan creado sin una, para garantizar un cálculo correcto de los resultados del test.</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.addArea}>
                <button className={styles.btnAddArea} onClick={handleAddArea}>
                    <IoIosAddCircleOutline/>
                    Añadir Área
                </button>
            </div>

            <div className={styles.opcionesDisponibles}>
                <h4>Áreas disponibles:</h4>
            </div>

            {areas.length === 0 ? (
                <div className={styles.emptyState}>
                    <FaCheckCircle className={styles.emptyIcon} />
                    <h3>No hay áreas de resultado disponibles</h3>
                    <p>¡Agrega un resultado para clasificar las respuestas!</p>
                </div>
            ) : (
                <div className={styles.areasList}>
                    {areas.map((area) => (
                        <div key={area.ar_clave} className={`${styles.areaCard} ${styles.testCard}`}>
                            <div className={styles.cardActions}>
                                <button
                                    className={styles.btnEdit}
                                    onClick={() => handleEditArea(area)}
                                    title="Editar área"
                                >
                                    <IoMdCreate/>
                                </button>
                                <button
                                    className={styles.btnDelete}
                                    onClick={() => handleDeleteArea(area.ar_clave, area.ar_codigo, area.ar_nombre)}
                                    title="Eliminar área"
                                >
                                    <FaTrash className={styles.iconBlack} />
                                </button>
                            </div>

                            <div className={styles.resTarjetaHeader}>
                                <h5>{area.ar_nombre}</h5>
                                <div className={styles.areaDescripcion}>
                                    <p>Codigo: {area.ar_codigo}</p>
                                </div>

                                <div>
                                    {area.ar_descripcion && area.ar_descripcion.trim() ? (
                                        <div className={styles.opcionesPreview}>
                                            <p>{area.ar_descripcion}</p>
                                        </div>
                                    ) : (
                                        <div className={styles.opcionesPreview}>
                                            <p>Sin descripción...</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Resultados;